import * as React from 'react'
import { cn } from '../../lib/utils'
import type { CSSVariableColor } from '../../types'

type TypographyElement =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'p'
  | 'span'
  | 'div'
  | 'label'
  | 'bdi'
  | 'caption'
type TypographySize =
  | '2xs'
  | 'xs'
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl'
  | '4xl'
  | '5xl'
  | '6xl'
type TypographyWeight = 'regular' | 'bold' | 'black'

interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
  variant?: TypographyElement
  size?: TypographySize
  weight?: TypographyWeight
  color?: CSSVariableColor
  truncate?: boolean
  htmlFor?: string
}

const sizeStyles: Record<TypographySize, string> = {
  '2xs': 'text-2xs', // 10px
  xs: 'text-xs', // 12px
  sm: 'text-sm', // 14px
  md: 'text-md', // 16px
  lg: 'text-lg', // 18px
  xl: 'text-xl', // 20px
  '2xl': 'text-2xl', // 24px
  '3xl': 'text-3xl', // 30px
  '4xl': 'text-4xl', // 36px
  '5xl': 'text-5xl', // 48px
  '6xl': 'text-6xl', // 60px
}

const weightStyles: Record<TypographyWeight, string> = {
  regular: 'font-regular',
  bold: 'font-bold',
  black: 'font-black',
}

const defaultElementSize: Record<TypographyElement, TypographySize> = {
  h1: '4xl',
  h2: '3xl',
  h3: '2xl',
  h4: 'xl',
  h5: 'lg',
  h6: 'sm',
  p: 'sm',
  span: 'sm',
  div: 'sm',
  label: 'sm',
  bdi: 'sm',
  caption: 'sm',
}

const defaultElementWeight: Record<TypographyElement, TypographyWeight> = {
  h1: 'bold',
  h2: 'bold',
  h3: 'bold',
  h4: 'black',
  h5: 'black',
  h6: 'black',
  p: 'regular',
  span: 'regular',
  div: 'regular',
  label: 'regular',
  bdi: 'regular',
  caption: 'regular',
}

function Typography({
  variant = 'span',
  size,
  weight,
  color,
  truncate = false,
  className,
  children,
  ...props
}: TypographyProps) {
  const Element = variant
  const finalSize = size || defaultElementSize[variant]
  const finalWeight = weight || defaultElementWeight[variant]

  return (
    <Element
      className={cn(
        sizeStyles[finalSize],
        weightStyles[finalWeight],
        color ? `${color}` : '',
        truncate && 'truncate',
        className,
      )}
      {...props}
    >
      {children}
    </Element>
  )
}

export { Typography }
export type {
  TypographyElement,
  TypographySize,
  TypographyWeight,
  TypographyProps,
}
